import React from 'react';
import PropTypes from 'prop-types';
import { Segment, Accordion, Form } from 'semantic-ui-react';
import { FormattedMessage, useIntl, defineMessages, injectIntl } from 'react-intl';
import {
  Icon,
  TextWidget,
  TextareaWidget,
  DatetimeWidget,
  WysiwygWidget,
  InlineForm,
} from '@plone/volto/components';
import { ColorListWidget } from 'design-comuni-plone-theme/components/ItaliaTheme';
import upSVG from '@plone/volto/icons/up-key.svg';
import downSVG from '@plone/volto/icons/down-key.svg';

const messages = defineMessages({
  title: {
    id: 'title',
    defaultMessage: 'Titolo',
  },
  card_title: {
    id: 'card_title',
    defaultMessage: 'Titolo della card',
  },
  card_text: {
    id: 'card_title_text',
    defaultMessage: "Testo della card",
  },
  card_date: {
    id: 'card_date',
    defaultMessage: 'Data della card',
  },
  bg_color: {
    id: 'bg_color',
    defaultMessage: 'Colore di sfondo del blocco',
  },
  card_title_color: {
    id: 'card_title_color',
    defaultMessage: 'Colore del titolo',
  },
  card_text_color: {
    id: 'card_text_color',
    defaultMessage: 'Colore del testo',
  },
  color_warning: {
    id: 'color_warning',
    defaultMessage: 'Giallo',
  },
  color_danger: {
    id: 'color_danger',
    defaultMessage: 'Rosso',
  },
  color_primary: {
    id: 'color_primary',
    defaultMessage: 'Primario',
  },
  color_secondary: {
    id: 'color_secondary',
    defaultMessage: 'Secondario',
  },
  color_black: {
    id: 'color_black',
    defaultMessage: 'Nero',
  },
  color_green: {
    id: 'color_green',
    defaultMessage: 'Verde',
  },
  link_more: {
    id: 'link_more',
    defaultMessage: 'Link ad altro',
  },
  link_more_title: {
    id: 'link_more_title',
    defaultMessage: 'Titolo del link ad altro',
  },
  link_more_href: {
    id: 'link_more_href',
    defaultMessage: 'Link',
  },
});

const Sidebar = ({
  data,
  block,
  onChangeBlock,
  onChangeSubBlock,
  selected = 0,
  setSelected,
}) => {
  const intl = useIntl();

  const bg_colors = [
    {
      name: 'secondary',
      label: intl.formatMessage(messages.color_secondary),
    },
    {
      name: 'danger',
      label: intl.formatMessage(messages.color_danger),
    },
    {
      name: 'primary',
      label: intl.formatMessage(messages.color_primary),
    },
    {
      name: 'warning',
      label: intl.formatMessage(messages.color_warning),
    },
  ]

  const text_colors = [
    {
      name: 'black',
      label: intl.formatMessage(messages.color_black),
    },
    {
      name: 'danger',
      label: intl.formatMessage(messages.color_danger),
    },
    {
      name: 'green',
      label: intl.formatMessage(messages.color_green),
    }
  ];

  const linkSchema = {
    fieldsets: [
      {
        id: 'default',
        title: intl.formatMessage(messages.link_more),
        fields: ['linkTitle', 'linkHref'],
      },
    ],
    properties: {
      linkTitle: {
        title: intl.formatMessage(messages.link_more_title),
      },
      linkHref: {
        title: intl.formatMessage(messages.link_more_href),
        widget: 'object_browser',
        mode: 'link',
        allowExternals: true,
      },
    },
    required: [],
  };

  return (
    <Form className="form-sidebar-timeline">
      <Segment.Group raised form>
        <header className="header pulled">
          <h2>
            <FormattedMessage
              id="TimelineBlock"
              defaultMessage="Timeline"
            />
          </h2>
        </header>
        <Segment>
          <TextWidget
            id="title"
            title={intl.formatMessage(messages.title)}
            value={data.title}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                [name]: value,
              });
            }}
          />
          <ColorListWidget
            id="bg_color"
            title={intl.formatMessage(messages.bg_color)}
            value={data.bg_color || bg_colors[0].name}
            colors={bg_colors}
            onChange={(name, value) => {
              onChangeBlock(block, {
                ...data,
                [name]: value,
              });
            }}
          />
        </Segment>
        <Accordion fluid styled className="form">
          {data.subblocks &&
            data.subblocks.map((subblock, index) => {
              return (
                <div key={'subblock' + index}>
                  <Accordion.Title
                    active={selected === index}
                    index={index}
                    onClick={() =>
                      setSelected(selected === index ? null : index)
                    }
                  >
                    {subblock?.card_date
                      ? `Card ${index + 1} — ${new Date(subblock.card_date).toLocaleDateString('it-IT')}`
                      : `Card ${index + 1}`}
                    {selected === index ? (
                      <Icon name={upSVG} size="20px" />
                    ) : (
                      <Icon name={downSVG} size="20px" />
                    )}
                  </Accordion.Title>
                  <Accordion.Content active={selected === index}>
                    <TextareaWidget
                      id="card_title"
                      title={intl.formatMessage(messages.card_title)}
                      value={subblock.card_title}
                      onChange={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                    <WysiwygWidget
                      id="card_text"
                      title={intl.formatMessage(messages.card_text)}
                      value={subblock.card_text}
                      onChange={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                    <DatetimeWidget
                      id="card_date"
                      title={intl.formatMessage(messages.card_date)}
                      value={subblock.card_date}
                      onChange={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                    <ColorListWidget
                      id="card_title_color"
                      title={intl.formatMessage(messages.card_title_color)}
                      value={subblock.card_title_color}
                      colors={text_colors}
                      onChange={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                    <ColorListWidget
                      id="card_text_color"
                      title={intl.formatMessage(messages.card_text_color)}
                      value={subblock.card_text_color}
                      colors={text_colors}
                      onChange={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                    <InlineForm
                      schema={linkSchema}
                      formData={subblock}
                      onChangeField={(name, value) => {
                        onChangeSubBlock(index, {
                          ...subblock,
                          [name]: value,
                        });
                      }}
                    />
                  </Accordion.Content>
                </div>
              );
            })}
        </Accordion>
      </Segment.Group>
    </Form>
  );
};

Sidebar.propTypes = {
  data: PropTypes.objectOf(PropTypes.any),
  block: PropTypes.string,
  onChangeBlock: PropTypes.func,
  selected: PropTypes.any,
  setSelected: PropTypes.func,
};

export default injectIntl(Sidebar);